// src/views/admin/ADEleves.jsx
//
// RÉVISION :
// - Onglets : Inscription / Réinscription.
// - Table enrichie : Type élève, Nouveau, Date d'inscription + totaux
//   (Masculins / Féminins / Plus jeune / Plus âgé) sur TOUT l'ensemble filtré.
// - Photo cliquable -> zoom.
// - Modal détail large + total scolarité + statut redoublant.
// - Le type d'élève d'une inscription existante est modifiable
//   depuis le modal détail (PATCH inscriptions/<id>/update/). Le total de
//   scolarité est recalculé juste après.
// - CORRIGÉ : getTypesEleve -> getTypesEleves (la liste des types était vide).
// - CORRIGÉ : à la modification, nationalité / état sanitaire / situation
//   sociale sont relus depuis le détail de l'élève (la liste ne les contient
//   pas) + option de secours si la valeur actuelle est inactive.
// - Il est maintenant possible de VIDER ces trois champs.
// - NOUVEAU : photo via le composant <PhotoField /> (prendre une photo avec la
//   caméra OU importer un fichier). La photo est recadrée en carré et
//   compressée (600 px, JPEG) avant envoi.
//
// PERMISSIONS (module "eleves") :
// - eleves.creer / importer / exporter / modifier / inscrire / desinscrire /
//   gerer_tuteurs. "Voir détails" reste visible pour tous.
import { useState, useEffect, useRef } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import PhotoField from "../../components/PhotoField";
import { useToastHook } from "../../utils/useToast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { urlimage } from "../../utils/config";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../context/PermissionsContext";
import ADReinscriptionMasse from "./ADReinscriptionMasse";


// ─── HELPER POUR LES URLs D'IMAGES ──────────────────────────────────────────
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${urlimage}${path}`;
  return `${urlimage}/${path}`;
};

// Normalise un champ qui peut être un id, une chaîne ou un objet {id}
const idOf = (v) =>
  v === null || v === undefined || v === "" ? "" : String(typeof v === "object" ? v.id : v);

// ─── COMPOSANTS ──────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth = 560, wide = false }) {
  return (
    <div className={`modal-overlay ${wide ? "modal-wide" : ""}`} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth, ...(wide ? { width: "100%" } : {}) }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Lightbox simple pour le zoom photo
function PhotoZoom({ url, onClose }) {
  if (!url) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, cursor: "zoom-out",
      }}
    >
      <img
        src={url}
        alt="Photo agrandie"
        style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, boxShadow: "0 4px 30px rgba(0,0,0,0.5)" }}
      />
    </div>
  );
}

// ─── CONSTANTES ──────────────────────────────────────────────────────────────
const EMPTY = {
  matricule: "",
  nom: "",
  prenom: "",
  date_naissance: "",
  ne_vers: false,
  annee_naissance_approximative: "",
  lieu_naissance: "",
  genre: "M",
  adresse: "",
  nationalite: "",
  etat_sanitaire: "",
  situation_sociale: "",
  telephone_parent: "",
  photo: null,
};

const GENRES = [
  { value: "M", label: "Masculin" },
  { value: "F", label: "Féminin" },
];

const EMPTY_TUTEUR = {
  lien: "pere",
  nom: "",
  prenom: "",
  profession: "",
  adresse: "",
  telephone: "",
};

const LIEN_LABELS = { pere: "Père", mere: "Mère" };

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────
export default function ADEleves({ slug }) {
  const navigate = useNavigate();
  const { canSee } = usePermissions();

  const peutCreer        = canSee("eleves.creer");
  const peutImporter     = canSee("eleves.importer");
  const peutExporter     = canSee("eleves.exporter");
  const peutModifier     = canSee("eleves.modifier");
  const peutInscrire     = canSee("eleves.inscrire");
  const peutDesinscrire  = canSee("eleves.desinscrire");
  const peutGererTuteurs = canSee("eleves.gerer_tuteurs");

  const [eleves, setEleves] = useState([]);
  const [classes, setClasses] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [anneeActive, setAnneeActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // ─── RÉFÉRENTIELS ──────────────────────────────────────────────────────
  const [nationalites, setNationalites] = useState([]);
  const [etatsSanitaires, setEtatsSanitaires] = useState([]);
  const [situationsSociales, setSituationsSociales] = useState([]);
  const [typesEleve, setTypesEleve] = useState([]);

  // ─── FILTRES ───────────────────────────────────────────────────────────
  const [searchEleves, setSearchEleves] = useState("");
  const [filterClasseEleves, setFilterClasseEleves] = useState("");
  const [filterAnneeEleves, setFilterAnneeEleves] = useState("");

  const [activeTab, setActiveTab] = useState("inscription");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formErrors, setFormErrors] = useState({});
  const [totalCount, setTotalCount] = useState(0);

  // ─── Pagination ────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // La liste ne se charge qu'une fois l'année active connue (sinon on
  // chargeait d'abord TOUS les élèves, puis la liste filtrée → "21" puis "9").
  const [filtresPrets, setFiltresPrets] = useState(false);
  // Compteurs pour ignorer une réponse arrivée après une requête plus récente.
  const listeReqRef = useRef(0);
  const statsReqRef = useRef(0);

  // ─── Totaux sur TOUT l'ensemble filtré ─────────────────────────────────
  const [statsEleves, setStatsEleves] = useState({
    masculins: 0, femininins: 0, plusJeune: null, plusAge: null,
  });

  // ─── Photo (zoom) ──────────────────────────────────────────────────────
  // L'aperçu et la capture sont gérés par <PhotoField />.
  const [photoZoom, setPhotoZoom] = useState(null);

  // ─── Total scolarité (modal détail) ────────────────────────────────────
  const [totalScolarite, setTotalScolarite] = useState(null);

  // ─── TUTEURS ───────────────────────────────────────────────────────────
  const [tuteurForm, setTuteurForm] = useState({ ...EMPTY_TUTEUR });
  const [tuteurSaving, setTuteurSaving] = useState(false);
  const [tuteurLierSearch, setTuteurLierSearch] = useState("");
  const [tuteurLierResults, setTuteurLierResults] = useState([]);
  const [tuteurLierSelected, setTuteurLierSelected] = useState(null);
  const [tuteurCible, setTuteurCible] = useState(null);

  // ─── Édition date d'inscription ────────────────────────────────────────
  const [editDateInscription, setEditDateInscription] = useState(false);
  const [dateInscriptionValue, setDateInscriptionValue] = useState("");
  const [dateInscriptionSaving, setDateInscriptionSaving] = useState(false);

  // ─── Édition type d'élève ──────────────────────────────────────────────
  const [editTypeEleve, setEditTypeEleve] = useState(false);
  const [typeEleveValue, setTypeEleveValue] = useState("");
  const [typeEleveSaving, setTypeEleveSaving] = useState(false);

  const toast = useToastHook();
  const adminService = useAdminService();
  const {
    getEleves, updateEleve, deleteEleve,
    getClasses, getAnneesScolaires,
    deleteInscription,
    getEleveDetail,
    getNationalites: getNationalitesFn,
    getEtatsSanitaires: getEtatsSanitairesFn,
    getSituationsSociales: getSituationsSocialesFn,
    createTuteur, updateTuteur, deleteTuteur,
    lierCompteTuteur, delierCompteTuteur,
    getAffectationsFrais,
  } = adminService;
  const getTypesEleveFn = adminService.getTypesEleves; // ← CORRIGÉ (était getTypesEleve)
  const updateInscriptionFn = adminService.updateInscription;

  // ─── CHARGEMENT ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (slug) {
      loadData();
      loadReferentiels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadReferentiels = async () => {
    try {
      const [nat, etats, situations] = await Promise.all([
        getNationalitesFn(slug),
        getEtatsSanitairesFn(slug, { actifs_seulement: "true" }),
        getSituationsSocialesFn(slug, { actifs_seulement: "true" }),
      ]);
      setNationalites(nat || []);
      setEtatsSanitaires(etats || []);
      setSituationsSociales(situations || []);
    } catch {
      toast.error("Impossible de charger les référentiels (nationalités, états sanitaires, situations sociales)");
    }
    if (getTypesEleveFn) {
      try {
        const data = await getTypesEleveFn(slug);
        setTypesEleve(data?.results || data || []);
      } catch {
        setTypesEleve([]);
      }
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchEleves, filterClasseEleves, filterAnneeEleves]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (slug && filtresPrets && activeTab === "inscription") {
        loadEleves();
        loadStatsEleves();
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchEleves, filterClasseEleves, filterAnneeEleves, page, pageSize, activeTab, filtresPrets]);

  useEffect(() => {
    if (filterClasseEleves && !classesPourFiltreEleves.find(c => String(c.id) === String(filterClasseEleves))) {
      setFilterClasseEleves("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAnneeEleves, classes]);

  // reinitialiserFiltres = true au premier chargement (année active, pas de
  // classe) ; false pour "Actualiser" afin de conserver les filtres choisis.
  const loadData = async (reinitialiserFiltres = true) => {
    setLoading(true);
    try {
      const [classesData, anneesData] = await Promise.all([
        getClasses(slug, { all_years: 'true', show_archived: 'true' }),
        getAnneesScolaires(slug)
      ]);
      setClasses(classesData || []);
      setAnnees(anneesData || []);
      const active = anneesData.find(a => a.actif) || null;
      setAnneeActive(active);
      if (reinitialiserFiltres) {
        setFilterAnneeEleves(prev => prev || String(active?.id || ""));
        setFilterClasseEleves("");
        setPage(1);
      }
      // La liste elle-même est chargée par l'effet sur les filtres, une fois
      // que le filtre "année" est réellement appliqué (évite le flash "21").
      setFiltresPrets(true);
    } catch (error) {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  const buildFiltres = () => {
    const params = {};
    if (searchEleves) params.search = searchEleves;
    if (filterClasseEleves) params.classe_id = filterClasseEleves;
    if (filterAnneeEleves) params.annee_id = filterAnneeEleves;
    return params;
  };

  const loadEleves = async () => {
    try {
      const reqId = ++listeReqRef.current;
      const params = { page, page_size: pageSize, ...buildFiltres() };
      const data = await getEleves(slug, params);
      if (reqId !== listeReqRef.current) return; // réponse obsolète

      let elevesData;
      if (data && data.results) {
        elevesData = data.results;
        setTotalCount(data.count || 0);
        setTotalPages(Math.ceil((data.count || 0) / pageSize));
      } else {
        elevesData = data || [];
        setTotalCount(elevesData.length);
        setTotalPages(1);
      }

      setEleves(elevesData);
    } catch (error) {
      toast.error("Erreur lors du chargement des élèves");
    }
  };

  const loadStatsEleves = async () => {
    try {
      const reqId = ++statsReqRef.current;
      const data = await getEleves(slug, { page_size: 'all', ...buildFiltres() });
      if (reqId !== statsReqRef.current) return; // réponse obsolète
      const liste = data.results || data || [];
      const masculins = liste.filter(e => e.genre === "M").length;
      const femininins = liste.filter(e => e.genre === "F").length;
      const ages = liste
        .map(e => calculerAgeNumerique(e.date_naissance, e.annee_naissance_approximative))
        .filter(a => a !== null);
      setStatsEleves({
        masculins,
        femininins,
        plusJeune: ages.length ? Math.min(...ages) : null,
        plusAge: ages.length ? Math.max(...ages) : null,
      });
    } catch {
      // Non bloquant
    }
  };

  // ─── PAGINATION ─────────────────────────────────────────────────────────
  // Bouton "Actualiser" : recharge référentiels + liste en CONSERVANT les
  // filtres courants (année, classe, recherche).
  const actualiser = async () => {
    await loadData(false);
    await loadEleves();
    await loadStatsEleves();
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) rangeWithDots.push(l + 1);
        else if (i - l !== 1) rangeWithDots.push("...");
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const paginationBtn = (isActive = false, isDisabled = false) => ({
    minWidth: "32px",
    height: "32px",
    padding: "0 8px",
    border: "1px solid",
    borderColor: isActive ? "#4a90e2" : "#dee2e6",
    borderRadius: "6px",
    background: isActive ? "#4a90e2" : "#ffffff",
    color: isActive ? "#ffffff" : isDisabled ? "#adb5bd" : "#495057",
    cursor: isDisabled ? "not-allowed" : "pointer",
    fontSize: "13px",
    fontWeight: isActive ? "600" : "400",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    opacity: isDisabled ? 0.6 : 1,
  });

  // ─── HELPERS ───────────────────────────────────────────────────────────────
  const classesPourFiltreEleves = classes.filter(
    c => !filterAnneeEleves || String(c.annee_scolaire) === String(filterAnneeEleves)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR");
    } catch {
      return dateStr;
    }
  };

  const getGenreLabel = (genre) => {
    if (genre === "M") return "Masculin";
    if (genre === "F") return "Féminin";
    return genre || "—";
  };

  const getLatestInscription = (eleve) => {
    if (!eleve.inscriptions || eleve.inscriptions.length === 0) return null;
    const sorted = [...eleve.inscriptions].sort((a, b) => {
      const dateA = new Date(a.annee_scolaire_detail?.date_debut || a.date_inscription || 0);
      const dateB = new Date(b.annee_scolaire_detail?.date_debut || b.date_inscription || 0);
      return dateB - dateA;
    });
    return sorted[0] || null;
  };

  const getInscriptionAnneeActive = (eleve) => {
    if (!eleve.inscriptions) return null;
    return eleve.inscriptions.find(i => i.annee_scolaire === anneeActive?.id) || null;
  };

  const getCurrentClasse = (eleve) => {
    const latest = getLatestInscription(eleve);
    return latest?.classe_nom || "Non inscrit";
  };

  const getCurrentTypeEleve = (eleve) => {
    const latest = getLatestInscription(eleve);
    return latest?.type_eleve_libelle || "—";
  };

  const getCurrentDateInscription = (eleve) => {
    const latest = getLatestInscription(eleve);
    return latest?.date_inscription || null;
  };

  const isNouveau = (eleve) => {
    const latest = getLatestInscription(eleve);
    return !!latest?.is_nouveau;
  };

  const getNomClasse = () => {
    if (!filterClasseEleves) return "Toutes les classes";
    const found = classes.find(c => String(c.id) === String(filterClasseEleves));
    return found ? found.nom : "Classe sélectionnée";
  };

  const calculerAgeNumerique = (dateStr, anneeApprox) => {
    if (dateStr) {
      try {
        const naissance = new Date(dateStr);
        const aujourd = new Date();
        let age = aujourd.getFullYear() - naissance.getFullYear();
        const m = aujourd.getMonth() - naissance.getMonth();
        if (m < 0 || (m === 0 && aujourd.getDate() < naissance.getDate())) age--;
        return age;
      } catch {
        return null;
      }
    }
    if (anneeApprox) {
      return new Date().getFullYear() - parseInt(anneeApprox);
    }
    return null;
  };

  const calculerAge = (dateStr, anneeApprox) => {
    const age = calculerAgeNumerique(dateStr, anneeApprox);
    if (age === null) return "—";
    return dateStr ? `${age} ans` : `≈ ${age} ans`;
  };

  const getNaissanceLabel = (eleve) => {
    if (eleve.ne_vers && eleve.annee_naissance_approximative) {
      return `Né(e) vers ${eleve.annee_naissance_approximative}`;
    }
    return formatDate(eleve.date_naissance);
  };

  // ─── CRUD ÉLÈVE ───────────────────────────────────────────────────────────
  // (la gestion de la photo — caméra / import / compression — est dans
  //  <PhotoField />, qui renvoie directement un File)
  const openCreate = () => {
    navigate(`/${slug}/admin/eleves/nouveau`);
  };

  const openDetail = async (e) => {
    setSelected(e);
    setModal("detail");
    setSelectedLoading(true);
    setTotalScolarite(null);
    setEditDateInscription(false);
    setEditTypeEleve(false);
    try {
      const full = await getEleveDetail(slug, e.id);
      setSelected(full);
      const latest = getLatestInscription(full);
      setDateInscriptionValue(latest?.date_inscription || "");
      await chargerTotalScolarite(full);
    } catch {
      toast.error("Impossible de charger tous les détails de l'élève");
    } finally {
      setSelectedLoading(false);
    }
  };

  const chargerTotalScolarite = async (eleve) => {
    const latest = getLatestInscription(eleve);
    if (!latest || !latest.classe || !latest.type_eleve || !getAffectationsFrais) {
      setTotalScolarite(null);
      return;
    }
    try {
      const affectations = await getAffectationsFrais(slug, {});
      const total = (affectations || [])
        .filter(a => a.classe === latest.classe && a.type_eleve === latest.type_eleve)
        .reduce((somme, a) => somme + Number(a.montant || 0), 0);
      setTotalScolarite(total);
    } catch {
      setTotalScolarite(null);
    }
  };

  const refreshSelectedDetail = async (id) => {
    try {
      const full = await getEleveDetail(slug, id);
      setSelected(full);
      await chargerTotalScolarite(full);
    } catch {
      toast.error("Impossible de rafraîchir la fiche de l'élève");
    }
  };

  // Ouvre le formulaire de modification. Les référentiels (nationalité, état
  // sanitaire, situation sociale) ne sont pas tous dans la liste : on relit
  // donc la fiche complète pour récupérer les valeurs actuelles.
  const openEdit = async (e) => {
    setSelected(e);
    setForm({
      matricule: e.matricule || "",
      nom: e.nom || "",
      prenom: e.prenom || "",
      date_naissance: e.date_naissance || "",
      ne_vers: e.ne_vers || false,
      annee_naissance_approximative: e.annee_naissance_approximative || "",
      lieu_naissance: e.lieu_naissance || "",
      genre: e.genre || "M",
      adresse: e.adresse || "",
      nationalite: idOf(e.nationalite),
      etat_sanitaire: idOf(e.etat_sanitaire),
      situation_sociale: idOf(e.situation_sociale),
      telephone_parent: e.telephone_parent || "",
      photo: null,
    });
    setFormErrors({});
    setModal("edit");

    try {
      const full = await getEleveDetail(slug, e.id);
      setSelected(full);
      setForm(prev => ({
        ...prev,
        nationalite: prev.nationalite || idOf(full.nationalite),
        etat_sanitaire: prev.etat_sanitaire || idOf(full.etat_sanitaire),
        situation_sociale: prev.situation_sociale || idOf(full.situation_sociale),
      }));
    } catch {
      // non bloquant
    }
  };

  const openDeleteOuDesinscrire = (e) => {
    const inscriptionCible = getInscriptionAnneeActive(e) || getLatestInscription(e);
    if (inscriptionCible) {
      setSelected({ eleve: e, inscription: inscriptionCible });
      setModal("desinscrire");
    } else {
      setSelected(e);
      setModal("delete");
    }
  };

  const openInscription = (e) => {
    navigate(`/${slug}/admin/eleves/${e.id}/inscrire`);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validerNaissance = (data, errors) => {
    if (data.ne_vers) {
      if (!data.annee_naissance_approximative) {
        errors.annee_naissance_approximative = "L'année de naissance est requise quand la date exacte est inconnue";
      }
    } else if (!data.date_naissance) {
      errors.date_naissance = "La date de naissance est requise (ou cochez « né vers »)";
    }
  };

  const handleSave = async () => {
    const errors = {};
    if (!form.nom.trim()) errors.nom = "Le nom est requis";
    if (!form.prenom.trim()) errors.prenom = "Le prénom est requis";
    if (!form.matricule.trim()) errors.matricule = "Le matricule est requis";
    validerNaissance(form, errors);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Veuillez corriger les erreurs du formulaire");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('matricule', form.matricule.trim().toUpperCase());
      formData.append('nom', form.nom.trim().toUpperCase());
      formData.append('prenom', form.prenom.trim());
      if (form.ne_vers) {
        formData.append('ne_vers', 'true');
        formData.append('annee_naissance_approximative', form.annee_naissance_approximative);
      } else {
        formData.append('ne_vers', 'false');
        formData.append('date_naissance', form.date_naissance);
      }
      formData.append('lieu_naissance', form.lieu_naissance?.trim() || "");
      formData.append('genre', form.genre || "M");
      formData.append('adresse', form.adresse?.trim() || "");
      // Chaîne vide = null côté DRF : permet aussi de VIDER ces champs
      formData.append('nationalite', form.nationalite || "");
      formData.append('etat_sanitaire', form.etat_sanitaire || "");
      formData.append('situation_sociale', form.situation_sociale || "");
      formData.append('telephone_parent', form.telephone_parent?.trim() || "");

      if (form.photo instanceof File) {
        formData.append('photo', form.photo);
      }

      await updateEleve(slug, selected.id, formData);
      toast.success(`Élève modifié avec succès`);
      setModal(null);
      await loadEleves();
      await loadStatsEleves();
    } catch (error) {
      let errorMessage = "Une erreur est survenue";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const fieldErrors = {};
          Object.keys(errorData).forEach(field => {
            const messages = Array.isArray(errorData[field]) ? errorData[field] : [errorData[field]];
            fieldErrors[field] = messages.join(" ");
          });
          setFormErrors(fieldErrors);
          errorMessage = Object.values(fieldErrors)[0] || "Veuillez vérifier les champs";
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteEleve(slug, selected.id);
      toast.success(`Élève supprimé avec succès`);
      setModal(null);
      await loadEleves();
      await loadStatsEleves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de supprimer cet élève");
    } finally {
      setSaving(false);
    }
  };

  const handleDesinscrire = async () => {
    setSaving(true);
    try {
      await deleteInscription(slug, selected.inscription.id);
      toast.success(`${selected.eleve.prenom} ${selected.eleve.nom} a été désinscrit(e) avec succès`);
      setModal(null);
      await loadEleves();
      await loadStatsEleves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de désinscrire cet élève");
    } finally {
      setSaving(false);
    }
  };

  // ─── ÉDITION DATE D'INSCRIPTION ───────────────────────────────────────────
  const handleSaveDateInscription = async () => {
    const latest = getLatestInscription(selected);
    if (!latest) return;
    if (!updateInscriptionFn) {
      toast.error("Fonction indisponible : ajoutez updateInscription() dans adminService.js");
      return;
    }
    setDateInscriptionSaving(true);
    try {
      await updateInscriptionFn(slug, latest.id, { date_inscription: dateInscriptionValue });
      toast.success("Date d'inscription mise à jour");
      setEditDateInscription(false);
      await refreshSelectedDetail(selected.id);
      await loadEleves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de modifier la date d'inscription");
    } finally {
      setDateInscriptionSaving(false);
    }
  };

  // ─── ÉDITION TYPE D'ÉLÈVE ─────────────────────────────────────────────────
  const handleSaveTypeEleve = async () => {
    const latest = getLatestInscription(selected);
    if (!latest) return;
    if (!typeEleveValue) {
      toast.error("Sélectionnez un type d'élève");
      return;
    }
    if (String(latest.type_eleve) === String(typeEleveValue)) {
      setEditTypeEleve(false);
      return;
    }
    if (!updateInscriptionFn) {
      toast.error("Fonction indisponible : ajoutez updateInscription() dans adminService.js");
      return;
    }
    setTypeEleveSaving(true);
    try {
      await updateInscriptionFn(slug, latest.id, { type_eleve: parseInt(typeEleveValue) });
      toast.success("Type d'élève modifié — la scolarité a été recalculée");
      setEditTypeEleve(false);
      await refreshSelectedDetail(selected.id); // recalcule aussi le total scolarité
      await loadEleves();
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
        error.response?.data?.type_eleve?.[0] ||
        error.response?.data?.detail ||
        "Impossible de modifier le type d'élève"
      );
    } finally {
      setTypeEleveSaving(false);
    }
  };

  // ─── TUTEURS ───────────────────────────────────────────────────────────────
  const getTuteur = (lien) => selected?.tuteurs?.find(t => t.lien === lien) || null;

  const openTuteurForm = (lien) => {
    const existant = getTuteur(lien);
    setTuteurCible(existant);
    setTuteurForm(existant
      ? {
          lien: existant.lien,
          nom: existant.nom || "",
          prenom: existant.prenom || "",
          profession: existant.profession || "",
          adresse: existant.adresse || "",
          telephone: existant.telephone || "",
        }
      : { ...EMPTY_TUTEUR, lien });
    setModal("tuteur-form");
  };

  const handleTuteurFormChange = (field, value) => {
    setTuteurForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveTuteur = async () => {
    setTuteurSaving(true);
    try {
      if (tuteurCible) {
        await updateTuteur(slug, tuteurCible.id, tuteurForm);
      } else {
        await createTuteur(slug, { ...tuteurForm, eleve: selected.id });
      }
      toast.success(`${LIEN_LABELS[tuteurForm.lien]} enregistré(e) avec succès`);
      setModal("detail");
      await refreshSelectedDetail(selected.id);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.non_field_errors?.[0] || data?.error || data?.detail || "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setTuteurSaving(false);
    }
  };

  const handleDeleteTuteur = async (tuteur) => {
    setTuteurSaving(true);
    try {
      await deleteTuteur(slug, tuteur.id);
      toast.success(`${LIEN_LABELS[tuteur.lien]} supprimé(e)`);
      await refreshSelectedDetail(selected.id);
    } catch {
      toast.error("Impossible de supprimer cette fiche");
    } finally {
      setTuteurSaving(false);
    }
  };

  const openTuteurLier = (tuteur) => {
    setTuteurCible(tuteur);
    setTuteurLierSearch(tuteur.telephone || "");
    setTuteurLierResults([]);
    setTuteurLierSelected(null);
    setModal("tuteur-lier");
  };

  useEffect(() => {
    if (modal !== "tuteur-lier") return;
    const t = setTimeout(async () => {
      try {
        const data = await adminService.getParents(slug, tuteurLierSearch ? { search: tuteurLierSearch } : {});
        setTuteurLierResults(data.results || data || []);
      } catch {
        // silencieux
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tuteurLierSearch, modal]);

  const handleLierTuteur = async () => {
    if (!tuteurLierSelected) {
      toast.error("Sélectionnez un compte parent");
      return;
    }
    setTuteurSaving(true);
    try {
      await lierCompteTuteur(slug, tuteurCible.id, tuteurLierSelected.id);
      toast.success("Compte lié avec succès");
      setModal("detail");
      await refreshSelectedDetail(selected.id);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de lier ce compte");
    } finally {
      setTuteurSaving(false);
    }
  };

  const handleDelierTuteur = async (tuteur) => {
    setTuteurSaving(true);
    try {
      await delierCompteTuteur(slug, tuteur.id);
      toast.success("Compte délié");
      await refreshSelectedDetail(selected.id);
    } catch {
      toast.error("Impossible de délier ce compte");
    } finally {
      setTuteurSaving(false);
    }
  };

  // ─── PDF / IMPRESSION ─────────────────────────────────────────────────────
  const genererPDFListe = async () => {
    setGeneratingPDF(true);
    try {
      const tousElevesResponse = await getEleves(slug, { page_size: 'all', ...buildFiltres() });
      const tousEleves = tousElevesResponse.results || tousElevesResponse || [];
      const nomClasse = getNomClasse();
      const dateGen = new Date().toLocaleDateString("fr-FR");
      const anneeNom = anneeActive?.nom || "";

      const doc = new jsPDF("portrait", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(16);
      doc.setTextColor(0, 64, 128);
      doc.setFont(undefined, "bold");
      doc.text("LISTE DES ÉLÈVES", pageWidth / 2, 18, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "normal");
      doc.text(`Classe : ${nomClasse}`, pageWidth / 2, 27, { align: "center" });

      if (anneeNom) {
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        doc.text(`Année scolaire : ${anneeNom}`, pageWidth / 2, 34, { align: "center" });
      }

      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Générée le ${dateGen} · ${tousEleves.length} élève(s)`,
        pageWidth / 2, 40, { align: "center" }
      );

      doc.setDrawColor(0, 64, 128);
      doc.setLineWidth(0.5);
      doc.line(14, 43, pageWidth - 14, 43);

      const body = tousEleves.map((e, i) => [
        i + 1,
        e.matricule || "—",
        `${e.nom} ${e.prenom}`,
        e.genre === "M" ? "M" : "F",
        getNaissanceLabel(e),
        calculerAge(e.date_naissance, e.annee_naissance_approximative),
      ]);

      autoTable(doc, {
        startY: 47,
        margin: { left: 14, right: 14 },
        head: [["N°", "Matricule", "Nom & Prénom", "Sexe", "Date de naissance", "Âge"]],
        body,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3, valign: "middle", textColor: [0, 0, 0] },
        headStyles: {
          fillColor: [0, 64, 128],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
          fontSize: 9,
        },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 28, halign: "center" },
          2: { cellWidth: 70, halign: "left" },
          3: { cellWidth: 14, halign: "center" },
          4: { cellWidth: 34, halign: "center" },
          5: { cellWidth: 20, halign: "center" },
        },
        alternateRowStyles: { fillColor: [240, 246, 255] },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Page ${data.pageNumber} / ${pageCount}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 8,
            { align: "center" }
          );
        },
      });

      const nomFichier = `liste_eleves_${nomClasse.replace(/\s+/g, "_")}_${dateGen.replace(/\//g, "-")}.pdf`;
      doc.save(nomFichier);
      toast.success("PDF généré avec succès");
    } catch (err) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const imprimerListe = async () => {
    try {
      const tousElevesResponse = await getEleves(slug, { page_size: 'all', ...buildFiltres() });
      const tousEleves = tousElevesResponse.results || tousElevesResponse || [];
      const nomClasse = getNomClasse();
      const dateGen = new Date().toLocaleDateString("fr-FR");
      const anneeNom = anneeActive?.nom || "";

      const lignes = tousEleves.map((e, i) => `
        <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
          <td style="padding:7px 10px;border:1px solid #ccc;text-align:center;">${i + 1}</td>
          <td style="padding:7px 10px;border:1px solid #ccc;text-align:center;">${e.matricule || "—"}</td>
          <td style="padding:7px 10px;border:1px solid #ccc;">${e.nom} ${e.prenom}</td>
          <td style="padding:7px 10px;border:1px solid #ccc;text-align:center;">${e.genre === "M" ? "M" : "F"}</td>
          <td style="padding:7px 10px;border:1px solid #ccc;text-align:center;">${getNaissanceLabel(e)}</td>
        </tr>
      `).join("");

      const html = `
        <!DOCTYPE html>
        <html>
          <head><meta charset="UTF-8"><title>Liste des élèves - ${nomClasse}</title>
            <style>
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body { font-family: 'Times New Roman', serif; padding: 20px; background: white; color: #000; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .header { text-align: center; margin-bottom: 18px; border-bottom: 2px solid #004080; padding-bottom: 12px; }
              .header h1 { font-size: 20pt; color: #004080; text-transform: uppercase; margin-bottom: 6px; }
              .header .sous-titre { font-size: 13pt; font-weight: bold; color: #222; margin-bottom: 4px; }
              .header .meta { font-size: 10pt; color: #555; }
              table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 10pt; }
              thead tr { background: #004080 !important; color: white; }
              thead th { padding: 8px 10px; border: 1px solid #003366; text-align: center; font-weight: bold; }
              tfoot td { font-size: 9pt; color: #666; text-align: center; padding-top: 10px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Liste des élèves</h1>
              <div class="sous-titre">Classe : ${nomClasse}</div>
              ${anneeNom ? `<div class="meta">Année scolaire : ${anneeNom}</div>` : ""}
              <div class="meta">Générée le ${dateGen} &nbsp;·&nbsp; ${tousEleves.length} élève(s)</div>
            </div>
            <table>
              <thead>
                <tr>
                  <th style="width:30px;">N°</th>
                  <th style="width:90px;">Matricule</th>
                  <th>Nom & Prénom</th>
                  <th style="width:40px;">Sexe</th>
                  <th style="width:110px;">Date de naissance</th>
                </tr>
              </thead>
              <tbody>${lignes}</tbody>
            </table>
            <script>
              window.onload = function() { setTimeout(function() { window.print(); }, 300); };
              window.onafterprint = function() { window.close(); };
            <\/script>
          </body>
        </html>
      `;

      const printWindow = window.open("", "_blank", "width=900,height=700,scrollbars=yes");
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
    } catch (err) {
      toast.error("Impossible d'imprimer la liste");
    }
  };

  // ─── RENDU ──────────────────────────────────────────────────────────────────
  if (loading) return <Loader />;

  return (
    <div>
      <div className="tab-nav">
        <button
          className={`tab-btn ${activeTab === "inscription" ? "active" : ""}`}
          onClick={() => setActiveTab("inscription")}
        >
          <i className="ti ti-file-check" /> INSCRIPTION ({totalCount})
        </button>
        {peutInscrire && (
          <button
            className={`tab-btn ${activeTab === "reinscription" ? "active" : ""}`}
            onClick={() => setActiveTab("reinscription")}
          >
            <i className="ti ti-refresh-dot" /> RÉINSCRIPTION
          </button>
        )}
      </div>

      {activeTab === "inscription" && (
        <>
          <div className="sb2">
            {peutCreer && (
              <button className="bprim" onClick={openCreate}>
                <i className="ti ti-plus" /> Nouvelle inscription
              </button>
            )}

            {peutImporter && (
              <button
                className="bprim"
                onClick={() => navigate(`/${slug}/admin/import-eleves`)}
                style={{ background: "#28a745" }}
                onMouseEnter={e => e.currentTarget.style.background = "#1e7e34"}
                onMouseLeave={e => e.currentTarget.style.background = "#28a745"}
              >
                <i className="ti ti-file-spreadsheet" /> Importer Excel
              </button>
            )}

            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou matricule..."
              value={searchEleves}
              onChange={e => setSearchEleves(e.target.value)}
              style={{ minWidth: 200 }}
            />

            <select
              value={filterAnneeEleves}
              onChange={e => { setFilterAnneeEleves(e.target.value); setFilterClasseEleves(""); }}
            >
              <option value="">Toutes les années</option>
              {annees.filter(a => !a.archived).map(a => (
                <option key={a.id} value={a.id}>{a.nom} {a.actif ? "(Active)" : ""}</option>
              ))}
            </select>

            <select value={filterClasseEleves} onChange={e => setFilterClasseEleves(e.target.value)}>
              <option value="">Toutes les classes</option>
              {filterAnneeEleves ? (
                classesPourFiltreEleves.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))
              ) : (
                Object.entries(
                  classesPourFiltreEleves.reduce((acc, c) => {
                    const key = c.annee_scolaire_nom || "Année inconnue";
                    (acc[key] ||= []).push(c);
                    return acc;
                  }, {})
                ).map(([annee, liste]) => (
                  <optgroup key={annee} label={annee}>
                    {liste.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </optgroup>
                ))
              )}
            </select>

            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={{ padding: "6px 10px", border: "1px solid var(--b)", borderRadius: "4px", fontSize: "13px", cursor: "pointer" }}
              title="Nombre d'élèves par page"
            >
              <option value="10">10/page</option>
              <option value="20">20/page</option>
              <option value="50">50/page</option>
              <option value="100">100/page</option>
            </select>

            <button className="bsec" onClick={actualiser}>
              <i className="ti ti-refresh" /> Actualiser
            </button>
          </div>

          {peutExporter && eleves.length > 0 && (
            <div className="export-actions">
              <button
                className="bsec"
                onClick={genererPDFListe}
                disabled={generatingPDF}
                style={{ color: "#dc3545", borderColor: "#dc3545" }}
              >
                <i className="ti ti-file-pdf" /> {generatingPDF ? "Génération..." : "Télécharger PDF"}
              </button>
              <button className="bsec" onClick={imprimerListe} style={{ color: "#6f42c1", borderColor: "#6f42c1" }}>
                <i className="ti ti-printer" /> Imprimer la liste
              </button>
              <span style={{ fontSize: 12, color: "var(--h)" }}>
                {filterClasseEleves ? "Liste complète de la classe" : "Sélectionnez une classe pour exporter"}
              </span>
            </div>
          )}

          <div className="card" style={{ padding: 0, overflow: "auto", marginBottom: 0 }}>
            <table className="tbl" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "6%" }}>Photo</th>
                  <th style={{ width: "9%" }}>Matricule</th>
                  <th style={{ width: "17%" }}>Nom complet</th>
                  <th style={{ width: "7%" }}>Genre</th>
                  <th style={{ width: "12%" }}>Classe</th>
                  <th style={{ width: "12%" }}>Type élève</th>
                  <th style={{ width: "7%" }}>Nouveau</th>
                  <th style={{ width: "10%" }}>Date inscription</th>
                  <th style={{ width: "20%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {eleves.length > 0 ? (
                  eleves.map(e => {
                    const latest = getLatestInscription(e);
                    const aUneInscription = !!latest;
                    const nomComplet = `${e.prenom} ${e.nom}`;
                    const classeActuelle = getCurrentClasse(e);
                    const typeEleveActuel = getCurrentTypeEleve(e);
                    return (
                      <tr key={e.id}>
                        <td style={{ textAlign: "center" }}>
                          {e.photo ? (
                            <img
                              src={getImageUrl(e.photo)}
                              alt="élève"
                              onClick={() => setPhotoZoom(getImageUrl(e.photo))}
                              style={{ width: 35, height: 35, borderRadius: "50%", objectFit: "cover", cursor: "zoom-in" }}
                              title="Cliquer pour agrandir"
                            />
                          ) : (
                            <span style={{ color: "var(--h)" }}>—</span>
                          )}
                        </td>
                        <td>
                          <span style={{
                            fontFamily: "'Space Grotesk',sans-serif",
                            fontWeight: 700, fontSize: 12,
                            background: "var(--bl-bg)", color: "var(--bl)",
                            padding: "3px 9px", borderRadius: 6
                          }}>
                            {e.matricule}
                          </span>
                        </td>
                        <td style={{ fontWeight: 500, whiteSpace: "normal", wordBreak: "break-word" }} title={nomComplet}>
                          {nomComplet}
                        </td>
                        <td>
                          <span className={`pill ${e.genre === "M" ? "info" : "pu"}`}>
                            {getGenreLabel(e.genre)}
                          </span>
                        </td>
                        <td style={{ whiteSpace: "normal", wordBreak: "break-word" }} title={classeActuelle}>
                          {classeActuelle}
                        </td>
                        <td style={{ fontSize: 13, whiteSpace: "normal", wordBreak: "break-word" }} title={typeEleveActuel}>
                          {typeEleveActuel}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {aUneInscription ? (
                            isNouveau(e)
                              ? <span className="pill info">Oui</span>
                              : <span className="pill">Non</span>
                          ) : "—"}
                        </td>
                        <td style={{ fontSize: 13, color: "var(--m)" }}>
                          {getCurrentDateInscription(e) ? formatDate(getCurrentDateInscription(e)) : "—"}
                        </td>
                        <td>
                          <div className="act">
                            <button
                              title="Voir détails"
                              onClick={() => openDetail(e)}
                              style={{ background: "#17a2b8", color: "white" }}
                              onMouseEnter={ev => ev.currentTarget.style.opacity = "0.8"}
                              onMouseLeave={ev => ev.currentTarget.style.opacity = "1"}
                            >
                              <i className="ti ti-eye" />
                            </button>

                            {peutModifier && (
                              <button
                                title="Modifier"
                                onClick={() => openEdit(e)}
                                style={{ background: "#ffc107", color: "black" }}
                                onMouseEnter={ev => ev.currentTarget.style.opacity = "0.8"}
                                onMouseLeave={ev => ev.currentTarget.style.opacity = "1"}
                              >
                                <i className="ti ti-edit" />
                              </button>
                            )}

                            {peutInscrire && (
                              <button
                                title={aUneInscription ? "Réinscrire" : "Inscrire"}
                                onClick={() => openInscription(e)}
                                style={{ background: "#28a745", color: "white" }}
                                onMouseEnter={ev => ev.currentTarget.style.opacity = "0.8"}
                                onMouseLeave={ev => ev.currentTarget.style.opacity = "1"}
                              >
                                <i className="ti ti-plus" />
                              </button>
                            )}

                            {peutDesinscrire && (
                              <button
                                className="del"
                                title={aUneInscription ? "Désinscrire" : "Supprimer"}
                                onClick={() => openDeleteOuDesinscrire(e)}
                              >
                                <i className={`ti ${aUneInscription ? "ti-user-off" : "ti-trash"}`} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
                      {searchEleves || filterClasseEleves ? "Aucun élève pour ces filtres." : "Aucun élève enregistré."}
                    </td>
                  </tr>
                )}
              </tbody>
              {eleves.length > 0 && (
                <tfoot>
                  <tr style={{ background: "var(--s2)", fontWeight: 600, fontSize: 13 }}>
                    <td colSpan="9">
                      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "6px 4px" }}>
                        <span>Total : <strong>{totalCount}</strong></span>
                        <span>Masculins : <strong>{statsEleves.masculins}</strong></span>
                        <span>Féminins : <strong>{statsEleves.femininins}</strong></span>
                        <span>Plus jeune : <strong>{statsEleves.plusJeune !== null ? `${statsEleves.plusJeune} ans` : "—"}</strong></span>
                        <span>Plus âgé : <strong>{statsEleves.plusAge !== null ? `${statsEleves.plusAge} ans` : "—"}</strong></span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {filterClasseEleves && filterAnneeEleves && String(filterAnneeEleves) !== String(anneeActive?.id) && (
            <div style={{ fontSize: 13, color: "#dc3545", marginTop: 10 }}>
              <i className="ti ti-alert-triangle" /> Classe d'une année passée — ces élèves ne sont pas
              forcément inscrits pour l'année active
            </div>
          )}

          {totalPages > 1 && (
            <div
              style={{
                display: "flex", justifyContent: "center", alignItems: "center", gap: "6px",
                marginTop: "20px", flexWrap: "wrap", padding: "16px", background: "var(--s2)", borderRadius: "8px",
              }}
            >
              <button onClick={() => handlePageChange(1)} disabled={page === 1} style={paginationBtn(false, page === 1)}>«</button>
              <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} style={paginationBtn(false, page === 1)}>‹</button>

              {getPageNumbers().map((p, index) => {
                if (p === "...") {
                  return <span key={`dots-${index}`} style={{ padding: "0 4px", color: "#adb5bd" }}>…</span>;
                }
                return (
                  <button key={p} onClick={() => handlePageChange(p)} style={paginationBtn(p === page)}>{p}</button>
                );
              })}

              <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} style={paginationBtn(false, page === totalPages)}>›</button>
              <button onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} style={paginationBtn(false, page === totalPages)}>»</button>

              <span style={{ marginLeft: "12px", fontSize: "13px", color: "#6c757d" }}>
                Page {page} / {totalPages} &nbsp;·&nbsp; {totalCount} élève{totalCount !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </>
      )}

      {activeTab === "reinscription" && peutInscrire && (
        <ADReinscriptionMasse slug={slug} />
      )}

      <PhotoZoom url={photoZoom} onClose={() => setPhotoZoom(null)} />

      {/* ─── MODAL DÉTAIL ─── */}
      {modal === "detail" && selected && (
        <Modal title="Détail de l'élève" onClose={() => setModal(null)} maxWidth={980} wide>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 460 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
                {selected.photo ? (
                  <img
                    src={getImageUrl(selected.photo)}
                    alt="Photo"
                    onClick={() => setPhotoZoom(getImageUrl(selected.photo))}
                    style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--pr)", cursor: "zoom-in" }}
                  />
                ) : (
                  <div style={{
                    width: 80, height: 80, borderRadius: "50%",
                    background: "var(--s2)", display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid var(--b)", fontSize: 30, color: "var(--h)"
                  }}>
                    <i className="ti ti-user" />
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{selected.prenom} {selected.nom}</div>
                  <div style={{ fontSize: 13, color: "var(--m)" }}>Matricule: {selected.matricule}</div>
                </div>
              </div>

              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px 24px",
                fontSize: 14, background: "var(--s2)", padding: 12, borderRadius: 8,
              }}>
                <div><span style={{ color: "var(--m)" }}>Genre :</span> {getGenreLabel(selected.genre)}</div>
                <div><span style={{ color: "var(--m)" }}>Naissance :</span> {getNaissanceLabel(selected)}</div>
                <div><span style={{ color: "var(--m)" }}>Lieu naissance :</span> {selected.lieu_naissance || "—"}</div>
                <div><span style={{ color: "var(--m)" }}>Adresse :</span> {selected.adresse || "—"}</div>
                <div><span style={{ color: "var(--m)" }}>Nationalité :</span> {selected.nationalite_nom || "—"}</div>
                <div><span style={{ color: "var(--m)" }}>État sanitaire :</span> {selected.etat_sanitaire_libelle || "—"}</div>
                <div><span style={{ color: "var(--m)" }}>Situation sociale :</span> {selected.situation_sociale_libelle || "—"}</div>
                <div><span style={{ color: "var(--m)" }}>Téléphone parent :</span> {selected.telephone_parent || "—"}</div>
              </div>

              {/* ─── Inscription courante ─── */}
              {(() => {
                const latest = getLatestInscription(selected);
                if (!latest) return (
                  <div style={{ marginTop: 12, fontSize: 13, color: "var(--h)" }}>Aucune inscription enregistrée</div>
                );
                return (
                  <div style={{
                    marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px 24px",
                    fontSize: 14, padding: 12, borderRadius: 8, border: "1px solid var(--b)",
                  }}>
                    <div><span style={{ color: "var(--m)" }}>Classe :</span> {latest.classe_nom}</div>
                    <div><span style={{ color: "var(--m)" }}>Nouveau :</span> {latest.is_nouveau ? "Oui" : "Non"}</div>
                    <div>
                      <span style={{ color: "var(--m)" }}>Redoublant :</span>{" "}
                      {latest.is_redoublant ? `Oui (${latest.classe_redoublee_nom || "—"})` : "Non"}
                    </div>

                    {/* ─── Type d'élève — modifiable ─── */}
                    <div style={{ gridColumn: "1 / -1" }}>
                      <span style={{ color: "var(--m)" }}>Type élève :</span>{" "}
                      {!editTypeEleve ? (
                        <>
                          {latest.type_eleve_libelle || "—"}
                          {peutInscrire && (
                            <button
                              className="bsec"
                              style={{ padding: "2px 8px", fontSize: 11, marginLeft: 8 }}
                              onClick={() => { setTypeEleveValue(idOf(latest.type_eleve)); setEditTypeEleve(true); }}
                            >
                              <i className="ti ti-edit" /> Modifier
                            </button>
                          )}
                        </>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <select
                            value={typeEleveValue}
                            onChange={e => setTypeEleveValue(e.target.value)}
                            style={{ padding: "4px 6px" }}
                          >
                            <option value="">— Sélectionner —</option>
                            {typesEleve.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                          </select>
                          <button
                            className="bprim"
                            style={{ padding: "3px 8px", fontSize: 12 }}
                            onClick={handleSaveTypeEleve}
                            disabled={typeEleveSaving}
                          >
                            {typeEleveSaving ? "..." : "Enregistrer"}
                          </button>
                          <button
                            className="bsec"
                            style={{ padding: "3px 8px", fontSize: 12 }}
                            onClick={() => setEditTypeEleve(false)}
                            disabled={typeEleveSaving}
                          >
                            Annuler
                          </button>
                          <span style={{ fontSize: 11, color: "var(--rd)", width: "100%" }}>
                            ⚠️ Le type détermine la scolarité à payer : le total sera recalculé.
                          </span>
                        </span>
                      )}
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <span style={{ color: "var(--m)" }}>Date d'inscription :</span>{" "}
                      {!editDateInscription ? (
                        <>
                          {formatDate(latest.date_inscription)}
                          {peutInscrire && (
                            <button
                              className="bsec"
                              style={{ padding: "2px 8px", fontSize: 11, marginLeft: 8 }}
                              onClick={() => { setDateInscriptionValue(latest.date_inscription || ""); setEditDateInscription(true); }}
                            >
                              <i className="ti ti-edit" /> Modifier
                            </button>
                          )}
                        </>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <input
                            type="date"
                            value={dateInscriptionValue}
                            onChange={e => setDateInscriptionValue(e.target.value)}
                            style={{ padding: "4px 6px" }}
                          />
                          <button className="bprim" style={{ padding: "3px 8px", fontSize: 12 }} onClick={handleSaveDateInscription} disabled={dateInscriptionSaving}>
                            {dateInscriptionSaving ? "..." : "Enregistrer"}
                          </button>
                          <button className="bsec" style={{ padding: "3px 8px", fontSize: 12 }} onClick={() => setEditDateInscription(false)}>
                            Annuler
                          </button>
                        </span>
                      )}
                    </div>

                    <div style={{
                      gridColumn: "1 / -1", marginTop: 4, padding: "8px 10px", borderRadius: 6,
                      background: "var(--bl-bg)", display: "flex", justifyContent: "space-between",
                      fontWeight: 700,
                    }}>
                      <span>Total frais de scolarité</span>
                      <span>{totalScolarite !== null ? `${totalScolarite.toLocaleString()} FCFA` : "—"}</span>
                    </div>
                  </div>
                );
              })()}

              {/* ─── Parent + Abonnement ─── */}
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 6 }}>
                    Parent (compte lié)
                  </div>
                  {selectedLoading ? (
                    <div style={{ fontSize: 13, color: "var(--h)" }}>Chargement...</div>
                  ) : selected.parent ? (
                    <div style={{ fontSize: 13, background: "var(--s2)", padding: "8px 10px", borderRadius: 6 }}>
                      <div style={{ fontWeight: 600 }}>
                        {selected.parent.first_name} {selected.parent.last_name}
                      </div>
                      <div style={{ color: "var(--m)" }}>
                        {[selected.parent.telephone1, selected.parent.telephone2].filter(Boolean).join(" / ") || "—"}
                        {selected.parent.email ? ` · ${selected.parent.email}` : ""}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "var(--h)" }}>Aucun parent associé</div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 6 }}>
                    Abonnement
                  </div>
                  {selectedLoading ? (
                    <div style={{ fontSize: 13, color: "var(--h)" }}>Chargement...</div>
                  ) : selected.abonnement_actif ? (
                    <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{selected.abonnement_actif.offre_nom} — jusqu'au {formatDate(selected.abonnement_actif.date_fin)}</span>
                      <span className={`pill ${selected.abonnement_actif.est_valide ? "info" : "pu"}`}>
                        {selected.abonnement_actif.est_valide ? "Actif" : "Expiré"}
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "var(--h)" }}>Aucun abonnement actif</div>
                  )}
                </div>
              </div>

              {/* ─── Tuteurs ─── */}
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 6 }}>
                  Tuteurs (identité déclarative)
                </div>
                {selectedLoading ? (
                  <div style={{ fontSize: 13, color: "var(--h)" }}>Chargement...</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {["pere", "mere"].map(lien => {
                      const t = getTuteur(lien);
                      return (
                        <div key={lien} style={{ background: "var(--s2)", padding: "8px 10px", borderRadius: 6, fontSize: 13 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontWeight: 600 }}>{LIEN_LABELS[lien]}</span>
                            {peutGererTuteurs && (
                              <div style={{ display: "flex", gap: 4 }}>
                                <button className="bsec" style={{ padding: "3px 8px", fontSize: 12 }} onClick={() => openTuteurForm(lien)}>
                                  <i className="ti ti-edit" /> {t ? "Modifier" : "Ajouter"}
                                </button>
                                {t && (
                                  <button
                                    className="bsec"
                                    style={{ padding: "3px 8px", fontSize: 12, color: "var(--rd)" }}
                                    onClick={() => handleDeleteTuteur(t)}
                                    disabled={tuteurSaving}
                                  >
                                    <i className="ti ti-trash" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          {t ? (
                            <div style={{ marginTop: 4, color: "var(--m)" }}>
                              <div>{t.nom} {t.prenom} {t.profession ? `· ${t.profession}` : ""}</div>
                              <div>{t.telephone || "—"} {t.adresse ? `· ${t.adresse}` : ""}</div>
                              <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                {t.utilisateur_detail ? (
                                  <>
                                    <span className="pill info">Compte lié</span>
                                    <span>{t.utilisateur_detail.first_name} {t.utilisateur_detail.last_name}</span>
                                    {peutGererTuteurs && (
                                      <button className="bsec" style={{ padding: "2px 6px", fontSize: 11 }} onClick={() => handleDelierTuteur(t)} disabled={tuteurSaving}>
                                        Délier
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  peutGererTuteurs && (
                                    <button className="bsec" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => openTuteurLier(t)}>
                                      <i className="ti ti-link" /> Lier un compte
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: 4, color: "var(--h)" }}>Non renseigné(e)</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {selected.inscriptions && selected.inscriptions.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 6 }}>
                    Historique des inscriptions
                  </div>
                  {selected.inscriptions.map((ins, idx) => (
                    <div key={idx} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "6px 10px", fontSize: 13,
                      background: idx % 2 === 0 ? "var(--s2)" : "transparent",
                      borderRadius: 4
                    }}>
                      <span>
                        {ins.classe_nom} ({ins.annee_scolaire_nom})
                        {ins.is_redoublant && <span style={{ color: "var(--h)" }}> · redouble {ins.classe_redoublee_nom || ""}</span>}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--m)" }}>
                        {formatDate(ins.date_inscription)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {peutInscrire && (
                <button className="bsec" style={{ marginTop: 12 }} onClick={() => openInscription(selected)}>
                  <i className="ti ti-plus" /> {getLatestInscription(selected) ? "Réinscrire" : "Inscrire"}
                </button>
              )}
            </div>

            {selected.code_qr && (
              <div style={{ textAlign: "center", flexShrink: 0, width: 170 }}>
                <div style={{
                  width: 160, height: 160, borderRadius: 8, border: "1px solid var(--b)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "var(--s2)", margin: "0 auto"
                }}>
                  <img
                    src={getImageUrl(selected.code_qr)}
                    alt="QR code"
                    style={{ width: 160, height: 160, borderRadius: 8 }}
                  />
                </div>
                <div style={{ fontSize: 12, color: "var(--m)", marginTop: 6 }}>
                  <i className="ti ti-qrcode" /> QR code élève
                </div>
              </div>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)}>Fermer</button>
            {peutModifier && (
              <button className="bprim" onClick={() => openEdit(selected)}>
                <i className="ti ti-edit" /> Modifier
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* ─── FICHE TUTEUR ─── */}
      {modal === "tuteur-form" && selected && (
        <Modal title={`${tuteurCible ? "Modifier" : "Ajouter"} — ${LIEN_LABELS[tuteurForm.lien]}`} onClose={() => setModal("detail")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Nom</label>
                <input value={tuteurForm.nom} onChange={e => handleTuteurFormChange('nom', e.target.value)} />
              </div>
              <div className="fg">
                <label>Prénom</label>
                <input value={tuteurForm.prenom} onChange={e => handleTuteurFormChange('prenom', e.target.value)} />
              </div>
            </div>
            <div className="fg">
              <label>Profession</label>
              <input value={tuteurForm.profession} onChange={e => handleTuteurFormChange('profession', e.target.value)} />
            </div>
            <div className="fg">
              <label>Adresse</label>
              <input value={tuteurForm.adresse} onChange={e => handleTuteurFormChange('adresse', e.target.value)} />
            </div>
            <div className="fg">
              <label>Téléphone</label>
              <input value={tuteurForm.telephone} onChange={e => handleTuteurFormChange('telephone', e.target.value)} />
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal("detail")} disabled={tuteurSaving}>Annuler</button>
            <button className="bprim" onClick={handleSaveTuteur} disabled={tuteurSaving}>
              {tuteurSaving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
                : <><i className="ti ti-check" /> Enregistrer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── LIER UN TUTEUR ─── */}
      {modal === "tuteur-lier" && tuteurCible && (
        <Modal title={`Lier un compte — ${LIEN_LABELS[tuteurCible.lien]}`} onClose={() => setModal("detail")}>
          <input
            placeholder="Rechercher un parent (nom, téléphone)..."
            value={tuteurLierSearch}
            onChange={e => { setTuteurLierSearch(e.target.value); setTuteurLierSelected(null); }}
            style={{ width: "100%" }}
          />
          <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}>
            {tuteurLierResults.map(p => (
              <div
                key={p.id}
                onClick={() => setTuteurLierSelected(p)}
                style={{
                  padding: "8px 10px", cursor: "pointer", borderRadius: 6, fontSize: 13,
                  background: tuteurLierSelected?.id === p.id ? "var(--bl-bg)" : "var(--s2)", marginBottom: 4,
                }}
              >
                <strong>{p.first_name} {p.last_name}</strong>
                {" — "}
                {[p.telephone1, p.telephone2].filter(Boolean).join(" / ") || "—"}
              </div>
            ))}
            {tuteurLierResults.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--h)" }}>Aucun résultat</div>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal("detail")} disabled={tuteurSaving}>Annuler</button>
            <button className="bprim" onClick={handleLierTuteur} disabled={tuteurSaving}>
              {tuteurSaving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
                : <><i className="ti ti-link" /> Lier</>}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── MODAL ÉDITION ÉLÈVE ─── */}
      {modal === "edit" && (
        <Modal title="Modifier l'élève" onClose={() => setModal(null)} maxWidth={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Matricule <span style={{ color: "var(--rd)" }}>*</span></label>
                <input
                  type="text" placeholder="Ex: ELEVE-001"
                  value={form.matricule}
                  onChange={e => handleFormChange('matricule', e.target.value.toUpperCase())}
                  style={formErrors.matricule ? { borderColor: "var(--rd)" } : {}}
                />
                {formErrors.matricule && (
                  <span style={{ color: "var(--rd)", fontSize: 12 }}>{formErrors.matricule}</span>
                )}
              </div>
              <div className="fg">
                <label>Genre</label>
                <select value={form.genre} onChange={e => handleFormChange('genre', e.target.value)}>
                  {GENRES.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="fgrid">
              <div className="fg">
                <label>Nom <span style={{ color: "var(--rd)" }}>*</span></label>
                <input
                  type="text" placeholder="Ex: OUEDRAOGO"
                  value={form.nom}
                  onChange={e => handleFormChange('nom', e.target.value.toUpperCase())}
                  style={formErrors.nom ? { borderColor: "var(--rd)" } : {}}
                />
                {formErrors.nom && (
                  <span style={{ color: "var(--rd)", fontSize: 12 }}>{formErrors.nom}</span>
                )}
              </div>
              <div className="fg">
                <label>Prénom <span style={{ color: "var(--rd)" }}>*</span></label>
                <input
                  type="text" placeholder="Ex: Fatima"
                  value={form.prenom}
                  onChange={e => handleFormChange('prenom', e.target.value)}
                  style={formErrors.prenom ? { borderColor: "var(--rd)" } : {}}
                />
                {formErrors.prenom && (
                  <span style={{ color: "var(--rd)", fontSize: 12 }}>{formErrors.prenom}</span>
                )}
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.ne_vers}
                onChange={e => handleFormChange('ne_vers', e.target.checked)}
              />
              Date de naissance exacte inconnue (« né(e) vers »)
            </label>

            {form.ne_vers ? (
              <div className="fg">
                <label>Année de naissance approximative <span style={{ color: "var(--rd)" }}>*</span></label>
                <input
                  type="number" placeholder="Ex: 2015"
                  value={form.annee_naissance_approximative}
                  onChange={e => handleFormChange('annee_naissance_approximative', e.target.value)}
                  style={formErrors.annee_naissance_approximative ? { borderColor: "var(--rd)" } : {}}
                />
                {formErrors.annee_naissance_approximative && (
                  <span style={{ color: "var(--rd)", fontSize: 12 }}>{formErrors.annee_naissance_approximative}</span>
                )}
              </div>
            ) : (
              <div className="fgrid">
                <div className="fg">
                  <label>Date de naissance <span style={{ color: "var(--rd)" }}>*</span></label>
                  <input
                    type="date"
                    value={form.date_naissance}
                    onChange={e => handleFormChange('date_naissance', e.target.value)}
                    style={formErrors.date_naissance ? { borderColor: "var(--rd)" } : {}}
                  />
                  {formErrors.date_naissance && (
                    <span style={{ color: "var(--rd)", fontSize: 12 }}>{formErrors.date_naissance}</span>
                  )}
                </div>
                <div className="fg">
                  <label>Lieu de naissance</label>
                  <input
                    type="text" placeholder="Ex: Ouagadougou"
                    value={form.lieu_naissance}
                    onChange={e => handleFormChange('lieu_naissance', e.target.value)}
                  />
                </div>
              </div>
            )}
            {form.ne_vers && (
              <div className="fg">
                <label>Lieu de naissance</label>
                <input
                  type="text" placeholder="Ex: Ouagadougou"
                  value={form.lieu_naissance}
                  onChange={e => handleFormChange('lieu_naissance', e.target.value)}
                />
              </div>
            )}

            <div className="fg">
              <label>Adresse</label>
              <input
                type="text" placeholder="Ex: Secteur 12, Ouagadougou"
                value={form.adresse}
                onChange={e => handleFormChange('adresse', e.target.value)}
              />
            </div>

            <div className="fgrid">
              <div className="fg">
                <label>Nationalité</label>
                <select value={form.nationalite} onChange={e => handleFormChange('nationalite', e.target.value)}>
                  <option value="">— Non renseignée —</option>
                  {form.nationalite && !nationalites.some(n => String(n.id) === String(form.nationalite)) && (
                    <option value={form.nationalite}>{selected?.nationalite_nom || "Valeur actuelle"}</option>
                  )}
                  {nationalites.map(n => <option key={n.id} value={n.id}>{n.nom}</option>)}
                </select>
              </div>
              <div className="fg">
                <label>État sanitaire</label>
                <select value={form.etat_sanitaire} onChange={e => handleFormChange('etat_sanitaire', e.target.value)}>
                  <option value="">— Non renseigné —</option>
                  {form.etat_sanitaire && !etatsSanitaires.some(x => String(x.id) === String(form.etat_sanitaire)) && (
                    <option value={form.etat_sanitaire}>{selected?.etat_sanitaire_libelle || "Valeur actuelle"}</option>
                  )}
                  {etatsSanitaires.map(x => <option key={x.id} value={x.id}>{x.libelle}</option>)}
                </select>
              </div>
            </div>
            <div className="fg">
              <label>Situation sociale</label>
              <select value={form.situation_sociale} onChange={e => handleFormChange('situation_sociale', e.target.value)}>
                <option value="">— Non renseignée —</option>
                {form.situation_sociale && !situationsSociales.some(x => String(x.id) === String(form.situation_sociale)) && (
                  <option value={form.situation_sociale}>{selected?.situation_sociale_libelle || "Valeur actuelle"}</option>
                )}
                {situationsSociales.map(s => <option key={s.id} value={s.id}>{s.libelle}</option>)}
              </select>
            </div>

            <div className="fg">
              <label>Téléphone parent</label>
              <input
                type="text" placeholder="Ex: +226 70 00 00 00"
                value={form.telephone_parent}
                onChange={e => handleFormChange('telephone_parent', e.target.value)}
              />
            </div>

            {/* ─── Photo : caméra ou import ─── */}
            <PhotoField
              value={form.photo}
              onChange={file => handleFormChange('photo', file)}
              currentUrl={selected?.photo ? getImageUrl(selected.photo) : null}
            />
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
                : <><i className="ti ti-check" /> Enregistrer</>}
            </button>
          </div>
        </Modal>
      )}

      {modal === "delete" && selected && (
        <Modal title="Supprimer l'élève" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            Supprimer l'élève <strong style={{ color: "var(--t)" }}>{selected.prenom} {selected.nom}</strong>
            {" "}({selected.matricule}) ?
            <div style={{ fontSize: 12, color: "var(--h)", marginTop: 6 }}>
              Cet élève n'a aucune inscription — la suppression est donc définitive et complète.
            </div>
            <div style={{ color: "var(--rd)", marginTop: 8, fontSize: 13 }}>
              ⚠️ Cette action est irréversible.
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Suppression...</>
                : <><i className="ti ti-trash" /> Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {modal === "desinscrire" && selected && (
        <Modal title="Désinscrire l'élève" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            Désinscrire <strong style={{ color: "var(--t)" }}>{selected.eleve.prenom} {selected.eleve.nom}</strong>
            {" "}de <strong style={{ color: "var(--t)" }}>{selected.inscription.classe_nom}</strong>
            {" "}({selected.inscription.annee_scolaire_nom}) ?
            <div style={{ fontSize: 12, color: "var(--h)", marginTop: 6 }}>
              Le dossier élève est conservé — seule cette inscription est supprimée. L'élève pourra
              être réinscrit plus tard.
            </div>
            <div style={{ color: "var(--rd)", marginTop: 8, fontSize: 13 }}>
              ⚠️ Cette action est irréversible.
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDesinscrire} disabled={saving}>
              {saving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
                : <><i className="ti ti-user-off" /> Désinscrire</>}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin { 
          0% { transform: rotate(0deg); } 
          100% { transform: rotate(360deg); } 
        }

        .tab-nav {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
          border-radius: 10px;
          border-bottom: 3px solid var(--or);
          padding: 8px 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: rgba(255,255,255,0.75);
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .tab-btn i { font-size: 18px; }

        .tab-btn:hover {
          background: rgba(255,255,255,0.12);
          color: var(--or-lt);
        }

        .tab-btn.active {
          background: white;
          color: var(--pr);
          box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
        }
        .tab-btn.active i { color: var(--or); }
        .tab-btn.active:hover { background: white; color: var(--pr); }

        .modal-overlay.modal-wide {
          padding-left: var(--sidebar-w, 260px);
          box-sizing: border-box;
        }
        @media (max-width: 992px) {
          .modal-overlay.modal-wide { padding-left: 0; }
        }

        .export-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          background: var(--s2);
          border-radius: 8px;
          border: 1px solid var(--b);
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .tbl td {
          vertical-align: top;
        }

        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          color: var(--m);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
          background: var(--s2);
        }

        .act button:hover { opacity: 0.8; }
        .act button.del { background: var(--rd); color: white; }
        .act button.del:hover { background: var(--rd-dk); }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}